import { useCallback, useEffect, useState } from 'react';

import { listProjectRepos } from '@/api/projects';
import type { ProjectRepository } from '@/types/project';

// --- Types ---

interface RepositorySelectorProps {
  projectKey: string;
  selectedRepo: string | null;
  onRepoChange: (repoName: string) => void;
}

// --- Sub-components ---

function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-2">
      <div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-700" />
      <div className="h-9 w-full rounded-md bg-gray-200 dark:bg-gray-700" />
    </div>
  );
}

// --- Main Component ---

export function RepositorySelector({ projectKey, selectedRepo, onRepoChange }: RepositorySelectorProps) {
  const [repos, setRepos] = useState<ProjectRepository[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRepos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listProjectRepos(projectKey);
      const sorted = [...data].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );
      setRepos(sorted);
    } catch {
      setError('Failed to load repositories. Please try again.');
      setRepos([]);
    } finally {
      setLoading(false);
    }
  }, [projectKey]);

  useEffect(() => {
    fetchRepos();
  }, [fetchRepos]);

  // Auto-select first repo when selectedRepo is null and list is non-empty
  useEffect(() => {
    const firstRepo = repos[0];
    if (!loading && firstRepo && selectedRepo === null) {
      onRepoChange(firstRepo.name);
    }
  }, [loading, repos, selectedRepo, onRepoChange]);

  // --- Render: Loading state ---
  if (loading) {
    return <LoadingSkeleton />;
  }

  // --- Render: Error state ---
  if (error) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3"
      >
        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
      </div>
    );
  }

  // --- Render: Empty state ---
  if (repos.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-4 text-center">
        <p className="text-sm text-content-tertiary">
          No repositories are linked to this project.
        </p>
      </div>
    );
  }

  // --- Render: Loaded state ---
  return (
    <div>
      <label
        htmlFor="repository-selector"
        className="block text-xs font-medium text-content-secondary mb-1"
      >
        Repository
      </label>
      <select
        id="repository-selector"
        value={selectedRepo ?? ''}
        onChange={(e) => onRepoChange(e.target.value)}
        className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
      >
        {repos.map((repo) => (
          <option key={repo.name} value={repo.name}>
            {repo.name}
          </option>
        ))}
      </select>
    </div>
  );
}
